import type { CallInfo } from '@compass/client/types/call';
import type { Evaluation } from '@compass/client/types/evaluation';
import type { Scorecard } from '@compass/client/types/scorecard';
import { Download, Loader2 } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { downloadQcEvaluationPdf } from '../utils/downloadQcEvaluationPdf';
import { QcEvaluationPdfReport } from './QcEvaluationPdfReport';

interface QcEvaluationPdfExportDialogProps {
  agentName?: string | null;
  call?: CallInfo | null;
  evaluation: Evaluation;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  scorecard?: Scorecard | null;
}

const safeFilenamePart = (value: string | undefined | null) =>
  value
    ?.trim()
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'evaluation';

const buildFilename = (evaluation: Evaluation, agentName?: string | null) => {
  const id = safeFilenamePart(evaluation.id || evaluation.call_id || 'evaluation');
  const agent = safeFilenamePart(agentName);
  return `qc-evaluation-${agent}-${id}.pdf`;
};

export const QcEvaluationPdfExportDialog: React.FC<QcEvaluationPdfExportDialogProps> = ({
  agentName,
  call,
  evaluation,
  onOpenChange,
  open,
  scorecard,
}) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleDownload = async () => {
    if (!reportRef.current) {
      toast.error('PDF preview is not ready yet.');
      return;
    }

    const toastId = toast.loading('Generating evaluation PDF...');
    setIsExporting(true);

    try {
      await downloadQcEvaluationPdf(reportRef.current, buildFilename(evaluation, agentName));
      toast.success('Evaluation PDF downloaded.', { id: toastId });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate evaluation PDF.';
      toast.error(message, { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[92vh] w-[min(1180px,calc(100vw-2rem))] max-w-none flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4 pr-14">
          <DialogTitle>Export evaluation PDF</DialogTitle>
          <DialogDescription>
            Preview includes evaluation details and complete scorecard data. Transcript is excluded.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-auto bg-muted/50 px-6 py-5">
          <QcEvaluationPdfReport
            ref={reportRef}
            evaluation={evaluation}
            scorecard={scorecard}
            call={call}
            agentName={agentName}
          />
        </div>

        <DialogFooter className="shrink-0 border-t bg-background px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="gap-2"
            onClick={handleDownload}
            disabled={isExporting}
            aria-busy={isExporting}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
